import { useMemo, useState } from "react";
import { useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Plus, MapPin, Crosshair, Trash2, Pencil, Wand2, Paintbrush } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useEntities, useMaps, usePins, useRegions, useInvalidate, useTimelines, useEvents, useQuotes } from "@/lib/queries";
import { quotesByEvent } from "@/lib/quoteLinks";
import { resolveRegion, timeRange } from "@/lib/regionTime";
import { MapTimeBar } from "@/components/RegionTime";
import { ShapeToolbar } from "@/components/ShapeToolbar";
import { Clock, Play, Pause } from "lucide-react";
import { usePlayback, pulseWindow } from "@/lib/playback";
import { PlaybackFeed } from "@/components/PlaybackLayers";
import { pathCandidates, pathColor } from "@/lib/travelPaths";
import { PathsPicker } from "@/components/PathsPicker";
import { JourneyPanel } from "@/components/JourneyPanel";
import { RegionDialog } from "@/components/RegionDialog";
import { Hexagon, Layers } from "lucide-react";
import { catColor } from "@/lib/lore";
import { MapCanvas } from "@/components/MapCanvas";
import { PinDialog } from "@/components/PinDialog";
import { MapUploadDialog } from "@/components/MapUploadDialog";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { MapGenerateDialog } from "@/components/MapGenerateDialog";
import { MapPainter } from "@/components/MapPainter";

function PinList({ pins, onEdit, onOpen, canEdit }) {
  if (!pins.length) return <p className="px-3 text-xs text-[#A8A090]">No pins yet.</p>;
  return (
    <div className="space-y-0.5" data-testid="pin-list">
      {pins.map((p) => (
        <div key={p.id} className="group flex items-center gap-2 rounded px-3 py-1.5 hover:bg-[#1A202C]">
          <MapPin className="h-3.5 w-3.5 shrink-0" style={{ color: catColor(p.entity?.category) }} />
          <button className="flex-1 text-left text-sm text-[#E8DFD1] truncate hover:text-[#F59E0B]" onClick={() => onOpen(p)} data-testid={`pin-list-item-${p.id}`}>{p.label || p.entity?.name || "Unnamed"}</button>
          {canEdit && <button className="md:opacity-0 md:group-hover:opacity-100 text-[#A8A090] hover:text-[#F3EBDD]" onClick={() => onEdit(p)} data-testid={`pin-edit-${p.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
        </div>
      ))}
    </div>
  );
}

export default function MapsPage() {
  const { universe, canEdit, openEntity, base } = useOutletContext();
  const { mid } = useParams();
  const navigate = useNavigate();
  const invalidate = useInvalidate();
  const { data: maps = [] } = useMaps(universe.id);
  const { data: entities = [] } = useEntities(universe.id);
  const current = maps.find((m) => m.id === mid) || maps[0];
  const { data: pins = [] } = usePins(current?.id);
  const { data: regions = [] } = useRegions(current?.id);
  const { data: timelines = [] } = useTimelines(universe.id);
  const [params] = useSearchParams();
  const [time, setTime] = useState(() => (params.get("t") != null ? { tid: params.get("timeline"), t: parseInt(params.get("t"), 10) } : null));
  const tid = time ? (timelines.some((t) => t.id === time.tid) ? time.tid : timelines[0]?.id) : null;
  const pathTid = tid || timelines[0]?.id;
  const { data: tEvents = [] } = useEvents(pathTid);
  const [pathIds, setPathIds] = useState([]);
  const { data: allQuotes = [] } = useQuotes(universe.id);
  const evQuotes = useMemo(() => quotesByEvent(allQuotes), [allQuotes]);
  const candidates = useMemo(() => pathCandidates(entities, tEvents, pins), [entities, tEvents, pins]);
  const colorOf = (id) => pathColor(pathIds.indexOf(id));
  const [focus, setFocus] = useState(null);
  const jumpTo = (s) => { setTime({ tid: pathTid, t: s.t }); setFocus({ x: s.pin.x_coord, y: s.pin.y_coord, k: Date.now() }); };
  const paths = candidates.filter((c) => pathIds.includes(c.entity.id)).map((c) => ({ ...c, color: colorOf(c.entity.id) }));
  const range = useMemo(() => timeRange(regions, tEvents), [regions, tEvents]);
  const timeOn = !!(time && tid);
  const yearT = time ? Math.floor(time.t) : null;
  const shown = useMemo(() => (timeOn ? regions.map((r) => resolveRegion(r, timelines, tid, yearT)) : regions), [timeOn, regions, timelines, tid, yearT]);
  const pb = usePlayback(time, setTime, range);
  const win = pulseWindow(pb.playing, pb.speed);
  const pinByEntity = useMemo(() => Object.fromEntries(pins.filter((p) => p.entity_id).map((p) => [p.entity_id, p])), [pins]);
  const names = useMemo(() => Object.fromEntries(entities.map((e) => [e.id, e.name])), [entities]);
  const startPlayback = () => pb.play(time && tid ? { tid, t: time.t } : { tid: timelines[0]?.id, t: range[0] });
  const visible = shown.filter((r) => !r.hidden);
  const [edit, setEdit] = useState(null);
  const startEdit = (r) => {
    pb.pause();
    const res = shown.find((x) => x.id === r.id) || r;
    setDraft(null); setPlacing(false);
    setEdit({ id: r.id, name: r.name, points: res.points, color: res.color, entity_id: res.entity_id, selected: null });
  };
  const saveShape = async () => {
    try {
      if (timeOn) await api.post(`/regions/${edit.id}/stages`, { timeline_id: tid, from_t: yearT, points: edit.points, color: edit.color, entity_id: edit.entity_id || null, hidden: false });
      else await api.patch(`/regions/${edit.id}`, { points: edit.points });
      await invalidate(["regions", current.id]);
      toast.success(timeOn ? `Shape saved from year ${yearT}` : "Shape saved");
      setEdit(null);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save shape");
    }
  };
  const toggleTime = () => { pb.pause(); setTime(time ? null : { tid: timelines[0]?.id, t: range[1] }); };
  const [draft, setDraft] = useState(null);
  const [regionDraft, setRegionDraft] = useState(null);
  const [layers, setLayers] = useState({ pins: true, regions: true, labels: false });
  const onDraft = (pts, done) => {
    if (!done) return setDraft(pts);
    setDraft(null);
    setRegionDraft({ points: pts.map((p) => [p.lng, p.lat]) });
  };
  const openRegion = (r) => (edit ? null : r.entity_id ? openEntity(r.entity_id) : canEdit ? setRegionDraft({ id: r.id }) : null);
  const dialogRegion = regionDraft?.id ? regions.find((r) => r.id === regionDraft.id) : regionDraft;
  const [uploading, setUploading] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [pinDraft, setPinDraft] = useState(null);
  const [delMap, setDelMap] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [painting, setPainting] = useState(false);
  const [savingGrid, setSavingGrid] = useState(false);
  const saveGrid = async (grid) => {
    setSavingGrid(true);
    await api.patch(`/maps/${current.id}/grid`, { grid });
    await invalidate(["maps", universe.id]);
    setSavingGrid(false);
    setPainting(false);
    toast.success("Map repainted");
  };

  const openPin = (p) => (p.entity_id ? openEntity(p.entity_id) : canEdit ? setPinDraft(p) : toast("This pin isn't linked to an entry"));
  const onMapClick = (ll) => {
    if (!placing || !canEdit) return;
    setPinDraft({ x_coord: ll.lng, y_coord: ll.lat });
    setPlacing(false);
  };
  const refresh = () => invalidate(["pins", current.id], ["maps", universe.id], "entity");
  const removeMap = async () => {
    await api.delete(`/maps/${current.id}`);
    setDelMap(false);
    invalidate(["maps", universe.id]);
    navigate(`${base}/maps`);
  };

  return (
    <div className="flex h-full flex-col md:flex-row">
      <div className="max-h-[32vh] w-full shrink-0 border-b border-[#2D3442] bg-[#0F131A]/60 flex flex-col overflow-y-auto lf-scroll md:max-h-none md:w-60 md:border-b-0 md:border-r" data-testid="maps-sidebar">
        <div className="p-5 pb-3 flex items-center justify-between">
          <div><div className="lf-label">Atlas</div><div className="font-display text-3xl">Maps</div></div>
          {canEdit && (
            <div className="flex">
              <Button size="icon" variant="ghost" onClick={() => setGenerating(true)} title="Generate map" data-testid="generate-map-button"><Wand2 className="h-4 w-4" /></Button>
              <Button size="icon" variant="ghost" onClick={() => setUploading(true)} title="Upload map" data-testid="add-map-button"><Plus className="h-4 w-4" /></Button>
            </div>
          )}
        </div>
        <div className="px-2 space-y-0.5" data-testid="map-list">
          {maps.map((m) => (
            <button key={m.id} onClick={() => { setPainting(false); navigate(`${base}/maps/${m.id}`); }} data-testid={`map-list-item-${m.id}`}
              className={`w-full text-left rounded px-3 py-2 text-sm transition-colors ${current?.id === m.id ? "bg-[#1A202C] text-[#F3EBDD] shadow-[inset_2px_0_0_#D97706]" : "text-[#A8A090] hover:bg-[#13171F] hover:text-[#F3EBDD]"}`}>
              {m.name}<span className="ml-2 text-xs opacity-60">{m.pin_count}</span>
            </button>
          ))}
        </div>
        {current && <div className="mt-6 px-2"><div className="lf-label px-3 mb-2">Pins</div><PinList pins={pins} onEdit={setPinDraft} onOpen={openPin} canEdit={canEdit} /></div>}
        {current && (
          <div className="mt-6 px-2 pb-6" data-testid="region-list">
            <div className="lf-label px-3 mb-2">Regions</div>
            {regions.length === 0 && <p className="px-3 text-xs text-[#A8A090]">No regions yet.</p>}
            {shown.map((r) => (
              <div key={r.id} className={`group flex items-center gap-2 rounded px-3 py-1.5 hover:bg-[#1A202C] ${r.hidden ? "opacity-45" : ""}`}>
                <span className="h-3 w-3 rounded-sm shrink-0" style={{ background: r.color, opacity: 0.8 }} />
                <button className="flex-1 text-left text-sm text-[#E8DFD1] truncate hover:text-[#F59E0B]" onClick={() => !r.hidden && openRegion(r)} data-testid={`region-list-item-${r.id}`}>
                  {r.name}{timeOn && r.hidden && <span className="ml-1 text-[10px] text-[#A8A090]" data-testid={`region-gone-${r.id}`}>(not yet / gone)</span>}
                  {timeOn && !r.hidden && r.entity && r.entity.name !== r.name && <span className="ml-1 text-[10px] text-[#A8A090]">· {r.entity.name}</span>}
                </button>
                {canEdit && <button className="md:opacity-0 md:group-hover:opacity-100 text-[#A8A090] hover:text-[#F3EBDD]" onClick={() => setRegionDraft({ id: r.id })} data-testid={`region-edit-${r.id}`}><Pencil className="h-3.5 w-3.5" /></button>}
              </div>
            ))}
          </div>
        )}
      </div>
      <div className="relative isolate min-h-[60vh] flex-1 md:min-h-0">
        {!current ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-10" data-testid="maps-empty">
            <p className="font-display text-4xl">No maps charted.</p>
            <p className="mt-2 text-sm text-[#A8A090] max-w-sm">Upload a hand-drawn continent, a city plan or a star chart, then pin your lore onto it.</p>
            {canEdit && (
              <div className="mt-6 flex gap-2">
                <Button onClick={() => setGenerating(true)} data-testid="maps-empty-generate-button"><Wand2 className="h-4 w-4 mr-1.5" />Generate from notes</Button>
                <Button variant="outline" onClick={() => setUploading(true)} data-testid="maps-empty-upload-button">Upload an image</Button>
              </div>
            )}
          </div>
        ) : painting ? (
          <MapPainter key={current.id} map={current} onSave={saveGrid} onCancel={() => setPainting(false)} saving={savingGrid} />
        ) : (
          <>
            <div className="absolute top-4 left-4 right-4 z-30 flex flex-wrap justify-end gap-2 rounded-md backdrop-blur-md bg-[#13171F]/80 border border-[#2D3442] p-1.5 sm:left-auto" data-testid="map-toolbar">
              <div className="hidden px-3 py-1.5 font-display text-lg sm:block" data-testid="current-map-name">{current.name}</div>
              <Button size="sm" variant={timeOn ? "default" : "ghost"} onClick={toggleTime} disabled={!timelines.length || !!edit} data-testid="map-time-toggle"><Clock className="h-4 w-4 mr-1.5" />{timeOn ? `Year ${yearT}` : "Time"}</Button>
              <Button size="sm" variant={pb.playing ? "default" : "ghost"} onClick={() => (pb.playing ? pb.pause() : startPlayback())} disabled={!timelines.length || !!edit} data-testid="map-playback-button">
                {pb.playing ? <Pause className="h-4 w-4 mr-1.5" /> : <Play className="h-4 w-4 mr-1.5" />}{pb.playing ? "Pause" : "Play"}
              </Button>
              <PathsPicker candidates={candidates} selected={pathIds} colorOf={colorOf} timelineName={timelines.find((x) => x.id === pathTid)?.name || "—"}
                onToggle={(id) => setPathIds((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]))} onClear={() => setPathIds([])} />
              {canEdit && !edit && (
                <>
                  {current.kind === "painted" && <Button size="sm" variant="ghost" onClick={() => { setPlacing(false); setPainting(true); }} data-testid="paint-map-button"><Paintbrush className="h-4 w-4 mr-1.5" />Paint</Button>}
                  <Button size="sm" variant={draft ? "default" : "ghost"} onClick={() => { setPlacing(false); setDraft(draft ? null : []); }} data-testid="draw-region-toggle"><Hexagon className="h-4 w-4 mr-1.5" />{draft ? (draft.length >= 3 ? "Click first corner to close" : "Click to place corners…") : "Draw region"}</Button>
                  <Button size="sm" variant={placing ? "default" : "ghost"} onClick={() => { setDraft(null); setPlacing(!placing); }} data-testid="drop-pin-toggle"><Crosshair className="h-4 w-4 mr-1.5" />{placing ? "Click the map…" : "Drop pin"}</Button>
                  <Button size="icon" variant="ghost" onClick={() => setDelMap(true)} data-testid="delete-map-button"><Trash2 className="h-4 w-4 text-red-400" /></Button>
                </>
              )}
            </div>
            <div className="absolute bottom-4 left-4 z-30 flex max-w-[calc(100%-2rem)] flex-wrap items-center gap-1 rounded-md backdrop-blur-md bg-[#13171F]/80 border border-[#2D3442] p-1" data-testid="layer-control">
              <Layers className="h-4 w-4 text-[#A8A090] mx-1.5" />
              {["pins", "regions", "labels"].map((k) => (
                <button key={k} onClick={() => setLayers((l) => ({ ...l, [k]: !l[k] }))} data-testid={`layer-toggle-${k}`}
                  className={`rounded px-2.5 py-1 text-xs capitalize transition-colors ${layers[k] ? "bg-[#D97706] text-[#0B0D10]" : "text-[#A8A090] hover:text-[#F3EBDD]"}`}>{k}</button>
              ))}
            </div>
            {timeOn && <MapTimeBar timelines={timelines} time={{ tid, t: time.t }} onChange={setTime} range={range} onClose={() => { pb.pause(); setTime(null); }} pb={pb} />}
            {timeOn && !edit && layers.pins && <PlaybackFeed events={tEvents} t={time.t} win={win} names={names} />}
            {edit && <ShapeToolbar edit={edit} onEdit={setEdit} onSave={saveShape} onCancel={() => setEdit(null)} label={timeOn ? `Save from year ${yearT}` : "Save shape"} />}
            <MapCanvas map={current} pins={pins} placing={placing} onMapClick={onMapClick} onPinClick={openPin}
              regions={visible} layers={layers} onRegionClick={openRegion} draft={draft} onDraft={onDraft} edit={edit} onEdit={setEdit}
              paths={edit ? [] : paths} pathT={timeOn ? time.t : null} focus={focus} smooth={pb.playing}
              pulses={timeOn && !edit ? { events: tEvents, pinByEntity, t: time.t, win } : null} />
            {!edit && paths.length > 0 && <JourneyPanel paths={paths} t={timeOn ? yearT : null} onJump={(s) => { pb.pause(); jumpTo(s); }} evQuotes={evQuotes} />}
          </>
        )}
      </div>
      {uploading && <MapUploadDialog open onOpenChange={setUploading} uid={universe.id} onSaved={(m) => { invalidate(["maps", universe.id]); navigate(`${base}/maps/${m.id}`); toast.success("Map added"); }} />}
      {generating && (
        <MapGenerateDialog open onOpenChange={setGenerating} uid={universe.id} locationCount={entities.filter((e) => e.category === "Location").length}
          onSaved={(m) => { invalidate(["maps", universe.id], "entity"); navigate(`${base}/maps/${m.id}`); toast.success(`${m.name} charted · ${m.pin_count} locations pinned`); }} />
      )}
      {dialogRegion && <RegionDialog open onOpenChange={(o) => !o && setRegionDraft(null)} mapId={current.id} region={dialogRegion} entities={entities}
        onSaved={() => invalidate(["regions", current.id], "entity")} timelines={timelines} time={timeOn ? { tid, t: yearT } : null}
        resolved={shown.find((r) => r.id === dialogRegion.id)} onEditShape={startEdit} />}
      {pinDraft && <PinDialog open onOpenChange={(o) => !o && setPinDraft(null)} mapId={current.id} pin={pinDraft} entities={entities} onSaved={refresh} />}
      <ConfirmDialog open={delMap} onOpenChange={setDelMap} onConfirm={removeMap} title="Delete this map?" description="The map and all its pins will be removed. Lore entries stay intact." />
    </div>
  );
}
